/**
 * V2 "Share photos" — the make-a-post intent, next to the plain Share
 * (link) button on content pages. One tap renders the page's card set
 * (share-card.js) and hands it to the native sheet; the Web Share API
 * fixes the payload before a target app is picked, so the two intents
 * live on two plainly-worded buttons instead of guessing.
 *
 * Views describe themselves as a card SOURCE (lazily — data may still
 * be loading at mount) and wire the handler into their delegated click
 * listener:
 *
 *   const sharePhotos = createSharePhotosHandler(el, () => rows && {...source});
 *   // in render: ${sharePhotosButton()} next to ${shareButton(...)}
 *   // in onClick: if (sharePhotos(event)) return;
 */
import { icon } from './icons.js';
import { canonicalUrl } from './share.js';
import { buildCardSet, shareCardSet } from './share-card.js';
import { markComplete } from '../features/community/tutorial.js';

/** Render the Share photos button (delegate via createSharePhotosHandler). */
export function sharePhotosButton() {
  return `
    <button type="button" class="v2-btn-share-photos" data-share-photos>
      ${icon('camera', 16)}<span data-share-photos-label>Share photos</span>
    </button>`;
}

/**
 * Per-mount click handler for the Share photos button.
 * @param {HTMLElement} el - the view's root (labels are found in it)
 * @param {() => object|null} getSource - the page as a card source;
 *   return null while data is still loading and the tap is ignored.
 * @returns {(event: Event) => boolean} true when the click was consumed
 */
export function createSharePhotosHandler(el, getSource) {
  let cardPromise = null;
  let busy = false;

  async function run() {
    const source = getSource();
    if (!source || busy) return;
    busy = true;
    markComplete('share');
    const setLabel = (text) => {
      const label = el.querySelector('[data-share-photos-label]');
      if (label) label.textContent = text;
    };
    setLabel('Making your card…');
    if (!cardPromise) cardPromise = buildCardSet(source);
    const outcome = await shareCardSet(source, canonicalUrl(), cardPromise);
    busy = false;
    if (!el.isConnected) return;
    if (outcome === 'copied' || outcome === 'failed') {
      // 'failed' usually means the build outran the tap's activation
      // window — the cards are memoized now, so the next tap is instant
      setLabel(outcome === 'copied' ? 'Link copied' : 'Ready — tap again');
      setTimeout(() => {
        if (el.isConnected) setLabel('Share photos');
      }, 2500);
      return;
    }
    setLabel('Share photos');
  }

  return function handle(event) {
    if (!event.target.closest('[data-share-photos]')) return false;
    run();
    return true;
  };
}
